import React, { useCallback, useEffect, useState } from "react";
import NoPropertiesToBidCard from "./cards/NoPropertiesToBidCard";
import WrapperPropertiesCard from "./cards/WrapperPropertiesCard";
import MapPin from "../../../../icons/MapPin";
import { useRouter } from "next/router";
import getKycStatus from "./getKycStatus";
import { useSelector } from "react-redux";

interface BidderKYCStatusContainerProps {
  kycPendingEvents: any[];
  upcomingAuction: any[];
  liveAuction: any[];
  userAllAuctionEvent: any[];
}

type DisplayType =
  | "no_properties_to_bid"
  | "single_properties_to_bid"
  | "more_properties_to_bid";

export default function BidderKYCStatusContainer({
  kycPendingEvents,
  upcomingAuction,
  liveAuction,
  userAllAuctionEvent,
}: BidderKYCStatusContainerProps) {
  const onboardingData = useSelector(
    (state: any) => state.bidderAuth.onboarding
  );
  const router = useRouter();
  const [activeBar, setActiveBar] = useState(0);
  const [type, setType] = useState<DisplayType>("no_properties_to_bid");
  const [filteredEvents, setFilteredEvents] = useState<any[]>([]);

  const initializeEvents = useCallback(() => {
    const events = userAllAuctionEvent.filter((event: any) => {
      return kycPendingEvents.some(
        (kycEvent: any) => kycEvent.event_uuid === event.uuid
      );
    });

    setFilteredEvents(events);
    setActiveBar(0);
    if (!events || events.length === 0) {
      setType("no_properties_to_bid");
    } else if (events.length === 1) {
      setType("single_properties_to_bid");
    } else {
      setType("more_properties_to_bid");
    }
  }, [userAllAuctionEvent, kycPendingEvents]);

  useEffect(() => {
    initializeEvents();
  }, [initializeEvents]);

  const handleCompleteKYC = async (eventIndex: number) => {
    const eventData = filteredEvents[eventIndex];
    const kycEvent = kycPendingEvents.find(
      (kycEvent: any) => kycEvent.event_uuid === eventData.uuid
    );

    if (kycEvent) {
      router.push(`/kyc/${kycEvent.uuid}`);
    } else {
      console.error("KYC event not found for event:", eventData.uuid);
    }
  };

  const getCurrentKycStatus = () => {
    if (filteredEvents?.length > 0) {
      const currentEvent = filteredEvents[activeBar] || filteredEvents[0];
      const bidder = currentEvent?.bidders?.find(
        (bidder: any) => bidder.user_uuid === onboardingData?.user_uuid
      );
      return getKycStatus(bidder);
    }
    return null;
  };

  // Constants
  const STAGING_IMAGE_ID = "27f785cc-24cb-4531-9b91-4a6c58bfaf37";
  const PRODUCTION_IMAGE_ID = "771263b2-a630-46f2-ae99-24cece88c4be";
  const DEFAULT_KYC_STATUS = "Application Incomplete";
  const INCOMPLETE_STATUS = "Incomplete Application";

  const getImageUrl = () => {
    const baseUrl = process.env.NEXT_PUBLIC_IMAGIX_URL;
    const imageId = baseUrl?.includes("staging")
      ? STAGING_IMAGE_ID
      : PRODUCTION_IMAGE_ID;
    return `${baseUrl}/${imageId}`;
  };

  const getCurrentEvent = () => {
    return filteredEvents[activeBar] || filteredEvents[0];
  };

  const getEventPropertyInfo = (event: any) => {
    const property = event?.property_dump;
    return {
      heading: `${property?.asset?.name} ${property?.sub_asset_type?.name}`,
      subHeading: `${property?.city?.name}, ${property?.state?.name}`,
    };
  };

  const getCtaText = (kycStatus: string | null) => {
    return kycStatus === INCOMPLETE_STATUS ? "Complete Now!" : "Check Now!";
  };

  const renderNoPropertiesCard = () => (
    <div>
      <NoPropertiesToBidCard
        hasUpcomingEvents={
          upcomingAuction?.length > 0 || liveAuction?.length > 0
        }
      />
    </div>
  );

  const renderSinglePropertyCard = () => {
    const event = getCurrentEvent();
    const propertyInfo = getEventPropertyInfo(event);
    const kycStatus = getCurrentKycStatus();

    return (
      <div className="w-full">
        <WrapperPropertiesCard
          onclickHandler={() => handleCompleteKYC(0)}
          cardType="kyc"
          showTag={true}
          id={event?.listing_id}
          tagText={kycStatus || DEFAULT_KYC_STATUS}
          imgLink={getImageUrl()}
          headingText={propertyInfo.heading}
          subHeadingText={propertyInfo.subHeading}
          showCTA={true}
          ctaText={getCtaText(kycStatus)}
          subHeadingIcon={<MapPin />}
          styles={{
            tagStyle: "bg-[#FFF3F3] text-[#B60100]",
            CTAStyle: "px-4 md:px-8 xl:px-14",
            imgStyle:
              "w-full h-[120px] mid:h-[130px] xl:h-[150px] min-w-[150px] mid:min-w-[160px] xl:min-w-[180px] object-cover",
          }}
        />
      </div>
    );
  };

  const renderMultiplePropertiesCard = () => {
    const event = getCurrentEvent();
    const propertyInfo = getEventPropertyInfo(event);
    const kycStatus = getCurrentKycStatus();

    return (
      <div className="">
        <WrapperPropertiesCard
          onclickHandler={() => handleCompleteKYC(activeBar)}
          cardType="kyc"
          showTag={true}
          id={event?.listing_id || false}
          tagText={kycStatus || DEFAULT_KYC_STATUS}
          imgLink={getImageUrl()}
          headingText={propertyInfo.heading}
          subHeadingText={propertyInfo.subHeading}
          showCTA={true}
          ctaText={getCtaText(kycStatus)}
          subHeadingIcon={<MapPin />}
          styles={{
            tagStyle: "bg-[#FFF3F3] text-[#B60100]",
            CTAStyle: "px-4 md:px-8 xl:px-14",
            imgStyle:
              "w-full h-[120px] mid:h-[130px] xl:h-[150px] min-w-[150px] mid:min-w-[160px] xl:min-w-[180px] object-cover",
          }}
        />

        <ul className="flex justify-center gap-3 mt-3 mb-6">
          {filteredEvents.map((item: any, index: number) => (
            <li
              key={`tab_list_id_${index}`}
              onClick={() => setActiveBar(index)}
              className={`w-[26px] h-1 rounded-full cursor-pointer ${
                activeBar === index ? "bg-brand-textBlack" : "bg-[#A8A8A8]"
              }`}
            />
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="min-w-[200px] w-full bg-white overflow-x-scroll">
      {type === "no_properties_to_bid" && renderNoPropertiesCard()}
      {type === "single_properties_to_bid" && renderSinglePropertyCard()}
      {type === "more_properties_to_bid" && renderMultiplePropertiesCard()}
    </div>
  );
}
